import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, Facebook, Instagram, Linkedin, MessageCircle } from "lucide-react";

// ============ NAVBAR ============
export const Navbar = () => {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "How It Works", path: "/how-it-works" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-sm"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_founder-ops-1/artifacts/p9z07uoa_Logo%20v2.png"
              alt="Done By Emma"
              className="h-14 w-auto"
            />
          </Link>
          
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium transition-all duration-200 relative ${
                  isActive(link.path) ? "text-[#2D4A6D]" : "text-slate-600 hover:text-[#2D4A6D]"
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-[#2D4A6D]" />
                )}
              </Link>
            ))}
            <Link to="/book">
              <button className="ml-4 bg-[#2D4A6D] hover:bg-[#1F3A5F] text-white px-6 py-2 text-sm font-medium">
                Book a Call
              </button>
            </Link>
          </div>

          <button onClick={() => setMobileMenu(!mobileMenu)} className="lg:hidden text-[#2D4A6D] p-2">
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenu && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenu(false)}
                className={`block px-4 py-3 text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? "bg-slate-50 text-[#2D4A6D] border-l-2 border-[#2D4A6D]"
                    : "text-slate-600 hover:bg-slate-50 hover:text-[#2D4A6D]"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 px-4">
              <Link to="/book" onClick={() => setMobileMenu(false)}>
                <button className="w-full bg-[#2D4A6D] hover:bg-[#1F3A5F] text-white py-3 text-sm font-medium">
                  Book a Call
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// ============ FOOTER ============
export const Footer = () => {
  return (
    <footer className="bg-[#2D4A6D] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_a9b36c2a-dda0-4533-9ce0-6305fab98ffb/artifacts/kmx7clya_Logo%20%286%29.png"
              alt="Done By Emma"
              className="h-16 w-auto mb-6"
            />
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              Operations. Systems. CRM. Automation.
            </p>
            <p className="text-white font-medium italic">If it's Emma, consider it done.</p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6 text-slate-300">Navigation</h4>
            <ul className="space-y-3">
              {[
                { name: "Home", path: "/" },
                { name: "Services", path: "/services" },
                { name: "How It Works", path: "/how-it-works" },
                { name: "About", path: "/about" },
                { name: "Contact", path: "/contact" },
                { name: "Book a Call", path: "/book" },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-slate-300 hover:text-white transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Capabilities */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6 text-slate-300">Services</h4>
            <ul className="space-y-3">
              <li><Link to="/services#operations" className="text-slate-300 hover:text-white transition-colors text-sm">Operations</Link></li>
              <li><Link to="/services#systems" className="text-slate-300 hover:text-white transition-colors text-sm">Systems</Link></li>
              <li><Link to="/services#crm" className="text-slate-300 hover:text-white transition-colors text-sm">CRM</Link></li>
              <li><Link to="/services#automation" className="text-slate-300 hover:text-white transition-colors text-sm">Automation</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6 text-slate-300">Contact</h4>
            <div className="space-y-4">
              <a href="mailto:hello@donebyemma.com" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors text-sm">
                <Mail className="w-4 h-4" /> hello@donebyemma.com
              </a>
              <a href="https://wa.me/639178118457" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors text-sm">
                <Phone className="w-4 h-4" /> +63 917 811 8457
              </a>
              <div className="flex gap-3 pt-4">
                <a href="https://www.facebook.com/donebyemma/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://www.instagram.com/donebyemma/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="https://www.linkedin.com/in/emmaponce/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
                <a href="http://m.me/donebyemma" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                  <MessageCircle className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="py-6 border-t border-white/10 text-center">
          <p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} Done By Emma. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// ============ LAYOUT ============
export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">{children}</main>
      <Footer />
    </div>
  );
};
